import { User } from '../../types/User';
import { Spacer } from '../common/Spacer';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useState, MouseEvent } from 'react';
import { BugReport, Opacity } from '@mui/icons-material';

export interface ConnectedToolbarProps {
  user: User;
  onLogoutClicked: () => void;
  onNewClicked: () => void;
  onOpenClicked: () => void;
  onOpenImportedFileClicked: () => void;
  onCloseClicked: () => void;
  onSwitchThemeTypeClicked: () => void;
  onBugReportClicked: () => void;
}

export const ConnectedToolbar = (props: ConnectedToolbarProps) => {
  const {
    user,
    onLogoutClicked,
    onNewClicked,
    onOpenClicked,
    onOpenImportedFileClicked,
    onCloseClicked,
    onSwitchThemeTypeClicked,
    onBugReportClicked,
  } = props;

  const [fileMenuEl, setFileMenuEl] = useState<HTMLElement | null>(null);
  const fileButtonClicked = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setFileMenuEl(event.currentTarget);
    },
    [],
  );
  const closeFileMenu = useCallback(() => {
    setFileMenuEl(null);
  }, []);

  const handleNewClicked = useCallback(() => {
    onNewClicked();
    closeFileMenu();
  }, [closeFileMenu, onNewClicked]);
  const handleOpenClicked = useCallback(() => {
    onOpenClicked();
    closeFileMenu();
  }, [closeFileMenu, onOpenClicked]);
  const handleOpenImportedClicked = useCallback(() => {
    onOpenImportedFileClicked();
    closeFileMenu();
  }, [closeFileMenu, onOpenImportedFileClicked]);
  const handleCloseClicked = useCallback(() => {
    onCloseClicked();
    closeFileMenu();
  }, [closeFileMenu, onCloseClicked]);

  const [accountMenuEl, setAccountMenuEl] = useState<HTMLElement | null>(null);
  const accountButtonClicked = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAccountMenuEl(event.currentTarget);
    },
    [],
  );
  const closeAccountMenu = useCallback(() => {
    setAccountMenuEl(null);
  }, []);

  const onLogoutItemClicked = useCallback(() => {
    onLogoutClicked();
    closeAccountMenu();
  }, [closeAccountMenu, onLogoutClicked]);

  return (
    <>
      <Button
        sx={{ height: '100%' }}
        aria-controls="file-menu"
        aria-haspopup="true"
        color={'inherit'}
        onClick={fileButtonClicked}
      >
        Fichier
      </Button>
      <Menu
        id={'file-menu'}
        anchorEl={fileMenuEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        open={fileMenuEl != null}
        onClose={closeFileMenu}
      >
        <MenuItem onClick={handleNewClicked}>Nouveau compte...</MenuItem>
        <MenuItem onClick={handleOpenClicked}>Ouvrir un compte...</MenuItem>
        <MenuItem onClick={handleOpenImportedClicked}>
          Ouvrir un compte importé...
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleCloseClicked}>Fermer le compte</MenuItem>
      </Menu>
      <Spacer />
      <Tooltip title={'Reporter un problème...'}>
        <IconButton color={'inherit'} onClick={onBugReportClicked} size="large">
          <BugReport />
        </IconButton>
      </Tooltip>
      <Tooltip title={'Passer au thème sombre/clair'}>
        <IconButton
          color={'inherit'}
          onClick={onSwitchThemeTypeClicked}
          size="large"
        >
          <Opacity />
        </IconButton>
      </Tooltip>
      <Button
        sx={{ height: '100%' }}
        aria-controls="account-menu"
        aria-haspopup="true"
        color={'inherit'}
        onClick={accountButtonClicked}
      >
        <Avatar
          alt={user.displayName}
          src={user.displayIconUrl}
          style={{ marginRight: 4 }}
        />
        {user.displayName}
      </Button>
      <Menu
        id={'account-menu'}
        anchorEl={accountMenuEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={accountMenuEl != null}
        onClose={closeAccountMenu}
      >
        <MenuItem onClick={onLogoutItemClicked}>Déconnexion</MenuItem>
      </Menu>
    </>
  );
};
